import React, { useEffect, useState, Children, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Moon, Check, X, Clock, Shield, Star, ArrowRight, ChevronDown } from 'lucide-react';
// --- Theme Constants ---
const COLORS = {
  charcoal: '#1a1c23',
  midnight: '#0c1222',
  cream: '#fdfbf7',
  teal: '#5a8f8f',
  lavender: '#8b7fc7',
  amber: '#d4a574',
  amberHover: '#c29463',
  textDark: '#e2e8f0',
  textLight: '#334155'
};
// --- Animation Variants ---
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
const itemVariant = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
// --- Modal Component ---
const Modal = ({
  isOpen,
  onClose,
  title,
  children
}: any) => {
  return <AnimatePresence data-id="element-2">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" data-id="element-3" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-4">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.2
        }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden pointer-events-auto" data-id="element-5">
              <div className="flex items-center justify-between p-6 border-b border-stone-200" data-id="element-6">
                <h2 className="text-2xl font-bold text-slate-900" data-id="element-7">{title}</h2>
                <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded-full transition-colors" data-id="element-8">
                  <X className="w-5 h-5 text-slate-600" data-id="element-9" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]" data-id="element-10">
                {children}
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
};
// --- Components ---
const Button = ({
  children,
  className = '',
  fullWidth = false,
  ...props
}: any) => <motion.button whileHover={{
  scale: 1.02
}} whileTap={{
  scale: 0.98
}} className={`
      px-6 md:px-8 py-3 md:py-4 rounded-full font-sans font-semibold text-white text-base md:text-lg shadow-lg
      transition-colors duration-300 flex items-center justify-center gap-2
      ${fullWidth ? 'w-full' : ''}
      ${className}
    `} style={{
  backgroundColor: COLORS.amber
}} {...props} data-id="element-11">
    {children}
  </motion.button>;
const Section = ({
  children,
  className = '',
  dark = false,
  ...props
}: {
  children: React.ReactNode;
  className?: string;
  dark?: boolean;
  [key: string]: any;
}) => <section className={`py-8 md:py-20 px-4 md:px-6 lg:px-12 overflow-hidden ${className}`} style={{
  backgroundColor: dark ? COLORS.charcoal : COLORS.cream,
  color: dark ? COLORS.textDark : COLORS.textLight
}} {...props} data-id="element-12">
    <div className="max-w-4xl mx-auto" data-id="element-13">{children}</div>
  </section>;
// --- Sales Notification Component (Mobile Only) ---
const SalesNotification = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentSale, setCurrentSale] = useState({
    name: '',
    location: ''
  });
  const names = [
  // USA
  {
    name: 'Sarah',
    location: 'Miami, USA'
  }, {
    name: 'Emma',
    location: 'Los Angeles, USA'
  }, {
    name: 'Jessica',
    location: 'New York, USA'
  }, {
    name: 'Ashley',
    location: 'Houston, USA'
  }, {
    name: 'Amanda',
    location: 'Chicago, USA'
  }, {
    name: 'Jennifer',
    location: 'Phoenix, USA'
  },
  // España
  {
    name: 'Ana',
    location: 'Madrid, España'
  }, {
    name: 'Sofía',
    location: 'Barcelona, España'
  }, {
    name: 'Carmen',
    location: 'Valencia, España'
  }, {
    name: 'Lucía',
    location: 'Sevilla, España'
  }, {
    name: 'Elena',
    location: 'Bilbao, España'
  },
  // México
  {
    name: 'María',
    location: 'Ciudad de México'
  }, {
    name: 'Fernanda',
    location: 'Monterrey, México'
  }, {
    name: 'Andrea',
    location: 'Guadalajara, México'
  },
  // Argentina
  {
    name: 'Laura',
    location: 'Buenos Aires, Argentina'
  }, {
    name: 'Martina',
    location: 'Córdoba, Argentina'
  },
  // Colombia
  {
    name: 'Camila',
    location: 'Bogotá, Colombia'
  }, {
    name: 'Carolina',
    location: 'Medellín, Colombia'
  },
  // Perú
  {
    name: 'Valentina',
    location: 'Lima, Perú'
  },
  // Chile
  {
    name: 'Isabella',
    location: 'Santiago, Chile'
  }];
  const getRandomSale = () => {
    const randomIndex = Math.floor(Math.random() * names.length);
    return names[randomIndex];
  };
  useEffect(() => {
    const showNotification = () => {
      setCurrentSale(getRandomSale());
      setIsVisible(true);
      setTimeout(() => setIsVisible(false), 4000);
    };
    const initialTimeout = setTimeout(() => showNotification(), 3000);
    const interval = setInterval(() => showNotification(), 9000);
    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, []);
  return <AnimatePresence data-id="element-14">
      {isVisible && <motion.div initial={{
      opacity: 0,
      y: 50,
      scale: 0.9
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 20,
      scale: 0.95
    }} transition={{
      type: 'spring',
      stiffness: 400,
      damping: 25
    }} className="fixed bottom-6 left-4 right-4 z-50 md:hidden" data-id="element-15">
          <div className="flex items-center gap-3 p-3 rounded-2xl shadow-lg border border-white/20" style={{
        background: 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)'
      }} data-id="element-16">
            <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 bg-stone-100" data-id="element-17">
              <img src="https://cdn.magicpatterns.com/uploads/q1WtKBVSPt6nZDrJQw55PS/Screenshot_2026-02-03_at_10.21.58_PM.png" alt="Bebé Duerme, Mamá Vive" className="w-full h-full object-cover" data-id="element-18" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-19">
              <p className="text-xs text-slate-500 font-medium" style={{
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif'
          }} data-id="element-20">
                Hace un momento
              </p>
              <p className="text-sm text-slate-900 font-semibold leading-tight" style={{
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif'
          }} data-id="element-21">
                {currentSale.name} de {currentSale.location}
              </p>
              <p className="text-xs text-slate-600" style={{
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif'
          }} data-id="element-22">
                compró{' '}
                <span className="font-medium" data-id="element-23">Bebé Duerme, Mamá Vive</span> por{' '}
                <span className="font-bold text-teal-600" data-id="element-24">$6.39</span>
              </p>
            </div>
            <div className="w-2 h-2 rounded-full bg-slate-300 flex-shrink-0" data-id="element-25"></div>
          </div>
        </motion.div>}
    </AnimatePresence>;
};
// --- FAQ Accordion Item Component ---
const FaqItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden" data-id="element-26">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between p-5 md:p-6 text-left" data-id="element-27">
        <span className="text-base md:text-lg font-semibold text-slate-900 pr-4" data-id="element-28">
          {question}
        </span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.2
      }} className="flex-shrink-0" data-id="element-29">
          <ChevronDown className="w-5 h-5 text-slate-400" data-id="element-30" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-31">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2
      }} data-id="element-32">
            <div className="px-5 md:px-6 pb-5 md:pb-6 pt-0" data-id="element-33">
              <p className="text-sm md:text-base text-slate-600" data-id="element-34">{answer}</p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export function LandingPage() {
  const [modalContent, setModalContent] = useState<'privacy' | 'terms' | null>(null);
  useEffect(() => {
    const initPixel = () => {
      if ((window as any).fbq) return;
      const n = ((window as any).fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      }) as any;
      if (!(window as any)._fbq) (window as any)._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      const t = document.createElement('script');
      t.async = true;
      t.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(t);
    };
    initPixel();
    (window as any).fbq('init', '826332227035064');
    (window as any).fbq('track', 'PageView');
  }, []);
  const handleCheckout = () => {
    if ((window as any).fbq) {
      ;
      (window as any).fbq('track', 'AddToCart', {
        content_name: 'Bebé Duerme, Mamá Vive',
        content_type: 'product',
        value: 6.39,
        currency: 'USD'
      });
    }
    // Small delay to ensure pixel fires before redirect
    setTimeout(() => {
      window.location.href = 'https://instaflowdp.com/cart/63119376777585:1';
    }, 300);
  };
  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen w-full overflow-x-hidden" data-id="element-35">
      <noscript data-id="element-36">
        <img height="1" width="1" style={{
        display: 'none'
      }} src="https://www.facebook.com/tr?id=826332227035064&ev=PageView&noscript=1" alt="" data-id="element-37" />
      </noscript>

      <SalesNotification data-id="element-38" />

      {/* --- 1) HERO SECTION --- */}
      <Section dark className="min-h-[90vh] md:min-h-[95vh] flex flex-col justify-center relative pt-16 md:pt-24 pb-16 md:pb-24" style={{
      background: 'linear-gradient(180deg, #0c1222 0%, #1a1f3a 50%, #0c1222 100%)'
    }} data-id="element-39">
        <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="text-center space-y-6 md:space-y-8 z-10" data-id="element-40">
          <motion.div variants={itemVariant} className="flex justify-center mb-2 md:mb-4" data-id="element-41">
            <span className="px-3 md:px-4 py-1 md:py-1.5 rounded-full bg-indigo-900/50 text-indigo-200 text-xs md:text-sm font-medium tracking-wide border border-indigo-700/50" data-id="element-42">
              GUÍA PDF DESCARGABLE
            </span>
          </motion.div>

          <motion.h1 variants={itemVariant} className="text-4xl md:text-7xl font-bold leading-tight text-white px-2" data-id="element-43">
            Un plan real de 7 noches para que tu{' '}
            <span style={{
            color: COLORS.amber
          }} data-id="element-44">
              bebé duerma
            </span>
            <br data-id="element-45" />y tú{' '}
            <span style={{
            color: COLORS.amber
          }} data-id="element-46">
              recuperes tu vida
            </span>
          </motion.h1>

          <motion.p variants={itemVariant} className="text-lg md:text-2xl text-indigo-100/80 max-w-2xl mx-auto leading-relaxed font-light px-2" data-id="element-47">
            Sin métodos rígidos, sin perfeccionismo y diseñado para
            implementarse hoy mismo{' '}
            <strong className="text-white" data-id="element-48">(incluso si son las 3 AM).</strong>
          </motion.p>

          <motion.div variants={itemVariant} className="flex flex-wrap justify-center gap-3 md:gap-6 px-4 pt-2" data-id="element-49">
            <div className="flex items-center gap-2 text-sm md:text-base text-indigo-200/90" data-id="element-50">
              <Check size={16} className="text-amber-400" data-id="element-51" />
              <span data-id="element-52">
                Diagnóstico en <strong data-id="element-53">60 segundos</strong>
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm md:text-base text-indigo-200/90" data-id="element-54">
              <Check size={16} className="text-amber-400" data-id="element-55" />
              <span data-id="element-56">
                <strong data-id="element-57">4 escenarios</strong> + guiones listos
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm md:text-base text-indigo-200/90" data-id="element-58">
              <Check size={16} className="text-amber-400" data-id="element-59" />
              <span data-id="element-60">
                <strong data-id="element-61">Plan de 7 noches</strong> (realista)
              </span>
            </div>
          </motion.div>

          {/* Hero Image */}
          <motion.div variants={itemVariant} className="pt-4 md:pt-6 px-4" data-id="element-62">
            <div className="max-w-md mx-auto rounded-xl md:rounded-2xl overflow-hidden shadow-2xl" data-id="element-63">
              <img src="https://cdn.magicpatterns.com/uploads/mnHs5iLUoomRHEpcvouxfV/mockup2-_BEBE.png" alt="Bebé Duerme Mamá Vive - Guía en el celular a las 3 AM" className="w-full h-auto" data-id="element-64" />
            </div>
          </motion.div>

          <motion.div variants={itemVariant} className="pt-6 md:pt-8 flex flex-col items-center gap-3 md:gap-4 px-2" data-id="element-65">
            <Button className="w-full md:w-auto md:min-w-[280px]" onClick={scrollToPricing} data-id="element-66">
              Quiero el mapa <ArrowRight className="w-4 h-4 md:w-5 md:h-5" data-id="element-67" />
            </Button>
            <p className="text-xs md:text-sm text-indigo-300/60" data-id="element-68">
              Descarga inmediata • PDF en español • Enlace también por email
            </p>
          </motion.div>
        </motion.div>

        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-30" data-id="element-69">
          <div className="absolute top-[-10%] right-[-5%] w-[300px] md:w-[500px] h-[300px] md:h-[500px] rounded-full blur-[80px] md:blur-[100px]" style={{
          background: COLORS.lavender
        }} data-id="element-70"></div>
          <div className="absolute bottom-[-10%] left-[-10%] w-[250px] md:w-[400px] h-[250px] md:h-[400px] rounded-full blur-[60px] md:blur-[80px]" style={{
          background: COLORS.amber
        }} data-id="element-71"></div>
          <div className="absolute inset-0 opacity-40" data-id="element-72">
            <div className="absolute top-[15%] left-[20%] w-1 h-1 bg-white rounded-full" data-id="element-73"></div>
            <div className="absolute top-[25%] right-[30%] w-0.5 h-0.5 bg-white rounded-full" data-id="element-74"></div>
            <div className="absolute top-[40%] left-[15%] w-0.5 h-0.5 bg-white rounded-full" data-id="element-75"></div>
            <div className="absolute top-[20%] right-[15%] w-1 h-1 bg-white rounded-full" data-id="element-76"></div>
            <div className="absolute top-[60%] left-[25%] w-0.5 h-0.5 bg-white rounded-full" data-id="element-77"></div>
            <div className="absolute top-[35%] right-[20%] w-0.5 h-0.5 bg-white rounded-full" data-id="element-78"></div>
          </div>
        </div>
      </Section>

      {/* --- 1.5) MINI DEMO --- */}
      <Section className="!pt-6 md:!pt-16" data-id="element-79">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="max-w-2xl mx-auto" data-id="element-80">
          <div className="text-center mb-8 md:mb-10" data-id="element-81">
            <span className="inline-block px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs md:text-sm font-semibold mb-3 md:mb-4" data-id="element-82">
              PRUEBA TANGIBLE
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-slate-900" data-id="element-83">
              Mini demo{' '}
              <span className="block text-lg md:text-xl font-normal text-slate-500 mt-2" data-id="element-84">
                (para que lo entiendas en 15 segundos)
              </span>
            </h2>
          </div>

          <div className="bg-gradient-to-br from-slate-50 to-stone-100 rounded-2xl p-6 md:p-8 border border-stone-200 shadow-sm" data-id="element-85">
            <div className="space-y-5 md:space-y-6" data-id="element-86">
              {/* Despertar */}
              <div className="flex gap-4" data-id="element-87">
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0 font-bold text-sm md:text-base" data-id="element-88">
                  !
                </div>
                <div data-id="element-89">
                  <p className="text-xs md:text-sm font-semibold text-red-600 uppercase tracking-wide mb-1" data-id="element-90">
                    Despertar
                  </p>
                  <p className="text-sm md:text-base text-slate-700" data-id="element-91">
                    Llora fuerte, se calma solo en brazos, no quiere comer.
                  </p>
                </div>
              </div>

              {/* Diagnóstico */}
              <div className="flex gap-4" data-id="element-92">
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm md:text-base text-white" style={{
                backgroundColor: COLORS.teal
              }} data-id="element-93">
                  60s
                </div>
                <div data-id="element-94">
                  <p className="text-xs md:text-sm font-semibold uppercase tracking-wide mb-1" style={{
                  color: COLORS.teal
                }} data-id="element-95">
                    Diagnóstico (60s)
                  </p>
                  <p className="text-sm md:text-base text-slate-700" data-id="element-96">
                    No parece dolor/enfermedad → no es "noche rara" →{' '}
                    <strong className="text-slate-900" data-id="element-97">
                      probable Asociación de Sueño
                    </strong>
                    .
                  </p>
                </div>
              </div>

              {/* Acción */}
              <div className="flex gap-4" data-id="element-98">
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center flex-shrink-0 text-white" style={{
                backgroundColor: COLORS.amber
              }} data-id="element-99">
                  <ArrowRight className="w-4 h-4 md:w-5 md:h-5" data-id="element-100" />
                </div>
                <div data-id="element-101">
                  <p className="text-xs md:text-sm font-semibold uppercase tracking-wide mb-1" style={{
                  color: COLORS.amber
                }} data-id="element-102">
                    Acción
                  </p>
                  <p className="text-sm md:text-base text-slate-700" data-id="element-103">
                    Ejecutas el{' '}
                    <strong className="text-slate-900" data-id="element-104">
                      Guión Escenario 2
                    </strong>{' '}
                    (1 intervención, 1 escalado máximo).
                  </p>
                </div>
              </div>

              {/* Cierre */}
              <div className="flex gap-4" data-id="element-105">
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center flex-shrink-0" data-id="element-106">
                  <Check className="w-4 h-4 md:w-5 md:h-5" data-id="element-107" />
                </div>
                <div data-id="element-108">
                  <p className="text-xs md:text-sm font-semibold text-indigo-600 uppercase tracking-wide mb-1" data-id="element-109">
                    Cierre
                  </p>
                  <p className="text-sm md:text-base text-slate-700" data-id="element-110">
                    Frase única + volver a acostar igual siempre.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 md:mt-8 pt-5 md:pt-6 border-t border-stone-300" data-id="element-111">
              <p className="text-center text-base md:text-lg font-semibold text-slate-900" data-id="element-112">
                Eso es.{' '}
                <span style={{
                color: COLORS.teal
              }} data-id="element-113">
                  Sin debate. Sin inventar. Sin castigo emocional.
                </span>
              </p>
            </div>
          </div>
        </motion.div>
      </Section>

      {/* --- 2) PROBLEM / IDENTIFICACIÓN --- */}
      <Section dark className="relative border-t border-slate-800" data-id="element-114">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeInUp} className="max-w-2xl mx-auto space-y-8 md:space-y-12" data-id="element-115">
          <h2 className="text-2xl md:text-4xl font-bold text-white text-center leading-tight" data-id="element-116">
            Te prometiste que "esta noche sería diferente".{' '}
            <span className="text-slate-400 font-normal" data-id="element-117">
              Aquí tienes el mapa exacto para que en 60 segundos sepas qué hacer
              en cada despertar, eliminando el zigzag de decisiones que agota a
              tu bebé y te rompe a ti.
            </span>
          </h2>

          <div className="space-y-4 md:space-y-6 text-base md:text-xl leading-relaxed text-slate-300" data-id="element-118">
            <p className="font-serif text-xl md:text-2xl text-white italic text-center" data-id="element-119">
              No es un "problema de sueño". Es un momento.
            </p>
            <p data-id="element-120">
              Son las 3:07 AM. Tu bebé llora. Tu cuerpo se activa como alarma. Y
              tú intentas hacer "lo correcto" con un cerebro a medias.
            </p>
            <p data-id="element-121">Aparecen las preguntas:</p>

            <ul className="space-y-3 md:space-y-4 pl-4 md:pl-6 border-l-2 border-slate-700 my-6 md:my-8 italic text-slate-400 text-sm md:text-base" data-id="element-122">
              <li data-id="element-123">"¿Tiene hambre o es costumbre?"</li>
              <li data-id="element-124">"¿Si lo cargo, lo acostumbro?"</li>
              <li data-id="element-125">"¿Por qué mi pareja no se mueve?"</li>
              <li data-id="element-126">
                Y la que más duele: "¿por qué siento rabia… y luego culpa?"
              </li>
            </ul>

            <p data-id="element-127">La parte más dura no es el llanto.</p>
            <p className="font-semibold text-white" data-id="element-128">
              Es el instante después, cuando piensas: "¿en serio acabo de
              reaccionar así?"
            </p>
            <p className="font-semibold text-amber-400 italic" data-id="element-129">
              Lo más peligroso de las 3 AM no es el llanto. Es que tomas
              decisiones permanentes con un cerebro apagado.
            </p>
          </div>

          <p className="text-center text-xl md:text-2xl font-bold text-white pt-4" data-id="element-130">
            No te falta amor.{' '}
            <span style={{
            color: COLORS.teal
          }} data-id="element-131">
              Te falta un sistema para cuando no te queda paciencia.
            </span>
          </p>
        </motion.div>
      </Section>

      {/* --- 3) CORE PAIN --- */}
      <div className="py-8 md:py-20 px-4 md:px-6 bg-gradient-to-b from-[#1a1c23] to-[#fdfbf7]" data-id="element-132">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="max-w-3xl mx-auto bg-white shadow-2xl rounded-xl md:rounded-2xl p-6 md:p-12 border border-stone-100 text-center" data-id="element-133">
          <h2 className="text-2xl md:text-4xl font-bold text-slate-900 mb-6 md:mb-8" data-id="element-134">
            Lo que te rompe no es una noche.
            <br data-id="element-135" />
            <span className="text-slate-600" data-id="element-136">Es la repetición.</span>
          </h2>

          <div className="space-y-4 md:space-y-5 text-left mb-8 md:mb-10" data-id="element-137">
            <div className="flex items-start gap-3" data-id="element-138">
              <div className="mt-1 p-1 rounded-full bg-red-100 text-red-600 flex-shrink-0" data-id="element-139">
                <X size={14} strokeWidth={3} data-id="element-140" />
              </div>
              <p className="text-sm md:text-base text-slate-700" data-id="element-141">
                Te prometes "hoy sí con calma"… y a las 3 AM vuelves a explotar
              </p>
            </div>
            <div className="flex items-start gap-3" data-id="element-142">
              <div className="mt-1 p-1 rounded-full bg-red-100 text-red-600 flex-shrink-0" data-id="element-143">
                <X size={14} strokeWidth={3} data-id="element-144" />
              </div>
              <p className="text-sm md:text-base text-slate-700" data-id="element-145">
                Tu pareja quiere ayudar. Pero si no hay guión... te pregunta
                "¿qué hacemos?" y tú sientes que cargas sola con todo
              </p>
            </div>
            <div className="flex items-start gap-3" data-id="element-146">
              <div className="mt-1 p-1 rounded-full bg-red-100 text-red-600 flex-shrink-0" data-id="element-147">
                <X size={14} strokeWidth={3} data-id="element-148" />
              </div>
              <p className="text-sm md:text-base text-slate-700" data-id="element-149">
                Amanece y aparece lo peor: culpa, vergüenza y silencio
              </p>
            </div>
          </div>

          <div className="w-12 md:w-16 h-1 mx-auto rounded-full mb-6 md:mb-8" style={{
          backgroundColor: COLORS.amber
        }} data-id="element-150"></div>

          <p className="text-lg md:text-xl font-semibold text-slate-900" data-id="element-151">
            No necesitas ser más paciente.{' '}
            <span style={{
            color: COLORS.teal
          }} data-id="element-152">
              Necesitas dejar de improvisar.
            </span>
          </p>
        </motion.div>
      </div>

      {/* --- 4) WHY OLD SOLUTIONS FAIL --- */}
      <Section className="bg-stone-100" data-id="element-153">
        <div className="max-w-2xl mx-auto" data-id="element-154">
          <h2 className="text-2xl md:text-4xl font-bold text-slate-900 text-center mb-8 md:mb-12" data-id="element-155">
            El error #1 a las 3 AM{' '}
            <span className="block text-lg md:text-2xl font-normal text-slate-500 mt-2" data-id="element-156">
              (que casi todas hacemos sin darnos cuenta)
            </span>
          </h2>

          <div className="space-y-6 md:space-y-8 text-base md:text-xl leading-relaxed text-slate-700" data-id="element-157">
            <p data-id="element-158">No es "cargarlo" o "no cargarlo". El error #1 es este:</p>

            <p className="text-lg md:text-xl font-semibold text-slate-900" data-id="element-159">
              Lo que más agota no es el llanto, es que tu pareja te pregunte:
              "¿Qué hacemos?" mientras tú intentas no explotar.
            </p>

            <p className="bg-white p-5 md:p-6 rounded-xl border-l-4 border-amber-400 italic text-slate-600" data-id="element-160">
              Esta guía incluye el{' '}
              <span className="text-slate-900 font-medium not-italic" data-id="element-161">
                Protocolo de Turnos y Acuerdos Claros.
              </span>{' '}
              Es un sistema "llave en mano" para que papá sepa exactamente
              cuándo le toca entrar, qué guion debe usar y cuándo es hambre
              real.
            </p>

            <div className="bg-slate-800 rounded-xl p-5 md:p-6 border-l-4 border-slate-500" data-id="element-162">
              <p className="text-base md:text-lg text-white leading-relaxed" data-id="element-163">
                Eliminas la discusión, eliminas el zigzag y, sobre todo,{' '}
                <span className="text-slate-300 italic" data-id="element-164">
                  eliminas la necesidad de ser la directora de orquesta a mitad
                  de la noche.
                </span>
              </p>
            </div>

            <p className="text-base md:text-lg text-slate-700 text-center" data-id="element-165">
              En este PDF lo corto de raíz con un método que se entiende rápido:{' '}
              <strong className="text-slate-900" data-id="element-166">
                4 escenarios. Una decisión. Un guión. Un cierre.
              </strong>
            </p>

            <p className="text-base md:text-lg text-slate-600 text-center italic" data-id="element-167">
              Basado en patrones repetitivos reales de despertares a esta edad.
            </p>

            <p className="text-center text-xl md:text-2xl font-bold pt-4" style={{
            color: COLORS.teal
          }} data-id="element-168">
              Te digo qué hacer en 60 segundos + "sin pensar" + "sin culpa"
            </p>
          </div>
        </div>
      </Section>

      {/* --- 5) PRODUCT INTRO --- */}
      <Section className="!pt-4 md:!pt-20" data-id="element-169">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} className="text-center max-w-2xl mx-auto" data-id="element-170">
          <motion.span variants={itemVariant} className="uppercase tracking-widest text-xs md:text-sm font-bold text-slate-400 mb-2 block" data-id="element-171">
            Presentamos
          </motion.span>
          <motion.h2 variants={itemVariant} className="text-3xl md:text-5xl font-bold text-slate-900 mb-4 md:mb-6" data-id="element-172">
            Bebé Duerme, <br data-id="element-173" />
            <span style={{
            color: COLORS.teal
          }} data-id="element-174">
              Mamá Vive
            </span>
          </motion.h2>

          <motion.div variants={itemVariant} className="mb-6 md:mb-8" data-id="element-175">
            <img src="https://cdn.magicpatterns.com/uploads/aDJpnZbHHdAcDHWFsrtEKK/Mockup_-_Bebe_duerme,_mama_vive.png" alt="Mamá leyendo Bebé Duerme, Mamá Vive en tablet" className="w-full max-w-md mx-auto rounded-xl shadow-lg" data-id="element-176" />
          </motion.div>

          <motion.p variants={itemVariant} className="text-lg md:text-xl text-slate-900 mb-3 md:mb-4 leading-relaxed font-bold" data-id="element-177">
            Este PDF fue creado para una etapa muy específica:
          </motion.p>
          <motion.p variants={itemVariant} className="text-lg md:text-xl text-slate-600 mb-6 md:mb-8 leading-relaxed font-[400]" data-id="element-178">
            bebé alrededor de los 11 meses, alimentación mixta y una dinámica
            donde papá ayuda… pero tú sigues liderando la noche.
          </motion.p>
          <motion.p variants={itemVariant} className="text-lg md:text-xl text-slate-600 mb-6 md:mb-8 leading-relaxed" data-id="element-179">
            No promete noches perfectas. Te da algo más raro y más valioso:{' '}
            <strong data-id="element-180">control</strong>.
          </motion.p>

          <motion.div variants={itemVariant} className="space-y-3 md:space-y-4 mb-6 md:mb-8 text-left max-w-md mx-auto" data-id="element-181">
            <p className="text-sm md:text-base font-semibold text-slate-900 mb-2" data-id="element-182">
              Control se ve así:
            </p>
            <div className="flex items-start gap-2 md:gap-3" data-id="element-183">
              <div className="mt-1 p-1 rounded-full bg-teal-100 text-teal-700 flex-shrink-0" data-id="element-184">
                <Check size={14} strokeWidth={3} className="md:w-4 md:h-4" data-id="element-185" />
              </div>
              <p className="text-sm md:text-base text-slate-700" data-id="element-186">
                No cambias de estrategia cinco veces
              </p>
            </div>
            <div className="flex items-start gap-2 md:gap-3" data-id="element-187">
              <div className="mt-1 p-1 rounded-full bg-teal-100 text-teal-700 flex-shrink-0" data-id="element-188">
                <Check size={14} strokeWidth={3} className="md:w-4 md:h-4" data-id="element-189" />
              </div>
              <p className="text-sm md:text-base text-slate-700" data-id="element-190">
                No discutes medio dormida
              </p>
            </div>
            <div className="flex items-start gap-2 md:gap-3" data-id="element-191">
              <div className="mt-1 p-1 rounded-full bg-teal-100 text-teal-700 flex-shrink-0" data-id="element-192">
                <Check size={14} strokeWidth={3} className="md:w-4 md:h-4" data-id="element-193" />
              </div>
              <p className="text-sm md:text-base text-slate-700" data-id="element-194">
                No dudas si es hambre o hábito
              </p>
            </div>
            <div className="flex items-start gap-2 md:gap-3" data-id="element-195">
              <div className="mt-1 p-1 rounded-full bg-teal-100 text-teal-700 flex-shrink-0" data-id="element-196">
                <Check size={14} strokeWidth={3} className="md:w-4 md:h-4" data-id="element-197" />
              </div>
              <p className="text-sm md:text-base text-slate-700" data-id="element-198">
                No te despiertas castigándote
              </p>
            </div>
          </motion.div>

          <motion.div variants={itemVariant} data-id="element-199">
            <p className="text-xl md:text-2xl font-bold text-center mb-4 md:mb-5" style={{
            color: COLORS.teal
          }} data-id="element-200">
              En 60 segundos sabrás qué hacer (y dejarás de improvisar)
            </p>
            <Button className="w-full md:w-auto" onClick={scrollToPricing} data-id="element-201">
              Quiero el mapa
            </Button>
            <p className="text-xs text-slate-500 mt-3" data-id="element-202">
              Lo descargas y lo usas esta noche
            </p>
          </motion.div>
        </motion.div>
      </Section>

      {/* --- 6) UNIQUE MECHANISM (4 SCENARIOS) --- */}
      <Section className="bg-stone-100" data-id="element-203">
        <div className="text-center max-w-2xl mx-auto mb-10 md:mb-16" data-id="element-204">
          <h2 className="text-2xl md:text-4xl font-bold mb-3 md:mb-4 text-slate-900" data-id="element-205">
            El Mapa de Noches
          </h2>
          <p className="text-base md:text-lg text-slate-600" data-id="element-206">
            4 escenarios para que dejes de adivinar y empieces a decidir.
          </p>
        </div>

        {/* Cómo funciona - 5 pasos */}
        <div className="max-w-2xl mx-auto mb-12 md:mb-16" data-id="element-207">
          <h3 className="text-xl md:text-2xl font-bold text-slate-900 text-center mb-8 md:mb-10" data-id="element-208">
            Cómo funciona{' '}
            <span className="text-slate-500 font-normal" data-id="element-209">(5 pasos)</span>
          </h3>
          <div className="space-y-4 md:space-y-5" data-id="element-210">
            {[{
            num: 1,
            text: 'Diagnóstico en 60 segundos'
          }, {
            num: 2,
            text: 'Identificas el escenario real'
          }, {
            num: 3,
            text: 'Ejecutas un guión (sin zigzag)'
          }, {
            num: 4,
            text: 'Haces el cierre repetible'
          }, {
            num: 5,
            text: 'Sigues el plan de 7 noches para estabilizar'
          }].map((step, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="flex items-center gap-4 bg-white p-4 md:p-5 rounded-xl shadow-sm border border-stone-200" data-id="element-211">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center text-white font-bold text-lg md:text-xl flex-shrink-0" style={{
              backgroundColor: COLORS.teal
            }} data-id="element-212">
                  {step.num}
                </div>
                <p className="text-base md:text-lg text-slate-800 font-medium" data-id="element-213">
                  {step.text}
                </p>
              </motion.div>)}
          </div>
        </div>

        <h3 className="text-xl md:text-2xl font-bold text-slate-900 text-center mb-6 md:mb-8" data-id="element-214">
          Los 4 escenarios:
        </h3>

        <div className="grid md:grid-cols-2 gap-4 md:gap-6" data-id="element-215">
          {[{
          title: 'Hambre / Alimentación',
          icon: <Moon className="w-5 h-5 md:w-6 md:h-6" data-id="element-216" />,
          desc: 'Distingue hambre real de búsqueda de confort sin confusión.'
        }, {
          title: 'Asociación de Sueño',
          icon: <Star className="w-5 h-5 md:w-6 md:h-6" data-id="element-217" />,
          desc: 'Cuando solo se duerme contigo, en brazos o de una sola forma.'
        }, {
          title: 'Malestar Real',
          icon: <Shield className="w-5 h-5 md:w-6 md:h-6" data-id="element-218" />,
          desc: 'Protocolos para dientes, incomodidad o enfermedad (sin desordenar toda la noche).'
        }, {
          title: 'Caos por Cambios',
          icon: <Clock className="w-5 h-5 md:w-6 md:h-6" data-id="element-219" />,
          desc: 'Regresiones, viajes y semanas raras: estructura sin reinventarlo todo.'
        }].map((item, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-stone-200" data-id="element-220">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center mb-3 md:mb-4" data-id="element-221">
                {item.icon}
              </div>
              <h3 className="text-lg md:text-xl font-bold text-slate-900 mb-2" data-id="element-222">
                {item.title}
              </h3>
              <p className="text-sm md:text-base text-slate-600" data-id="element-223">{item.desc}</p>
            </motion.div>)}
        </div>

        <p className="text-center text-lg md:text-xl font-semibold text-slate-900 mt-10 md:mt-14" data-id="element-224">
          A las 3 AM no decides con culpa.{' '}
          <span style={{
          color: COLORS.teal
        }} data-id="element-225">
            Decides con mapa.
          </span>
        </p>
      </Section>

      {/* --- 7) WHAT'S INCLUDED --- */}
      <Section className="!pt-2 md:!pt-20" data-id="element-226">
        <div className="bg-slate-900 rounded-2xl md:rounded-3xl p-6 md:p-16 text-white relative overflow-hidden" data-id="element-227">
          <div className="absolute top-0 right-0 w-48 h-48 md:w-64 md:h-64 bg-teal-900 rounded-full blur-[60px] md:blur-[80px] opacity-50" data-id="element-228"></div>

          <div className="relative z-10" data-id="element-229">
            <div className="text-center mb-8 md:mb-12" data-id="element-230">
              <h2 className="text-2xl md:text-4xl font-bold mb-3 md:mb-4" data-id="element-231">
                Qué incluye exactamente
              </h2>
              <p className="text-slate-300 text-base md:text-lg" data-id="element-232">
                Diseñado para leerse medio dormida (y volver a usarlo mil
                veces).
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center" data-id="element-233">
              <div className="flex justify-center" data-id="element-234">
                <img src="https://cdn.magicpatterns.com/uploads/efBpYGfQPcZyKwuWGPmxJM/mockup_landing_page_2_-_bebe.png" alt="Bebé Duerme, Mamá Vive - Vista previa del PDF" className="w-full max-w-md rounded-xl shadow-2xl" data-id="element-235" />
              </div>

              <div data-id="element-236">
                <ul className="space-y-4 md:space-y-6 mb-6 md:mb-8" data-id="element-237">
                  {['El Mapa de Noches (los 4 escenarios + diagnóstico rápido)', 'Guiones listos para las 3 AM (tú + tu pareja)', 'Protocolo de turnos y acuerdos claros', 'Plan de 7 noches (paso a paso, sin perfeccionismo)', 'Checklists imprimibles para la mesa de noche'].map((item, i) => <motion.li key={i} initial={{
                  opacity: 0,
                  x: 20
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  delay: i * 0.1
                }} className="flex items-start gap-3 md:gap-4" data-id="element-238">
                      <div className="mt-0.5 md:mt-1 min-w-[20px] md:min-w-[24px] h-5 md:h-6 rounded-full border border-amber-500/50 flex items-center justify-center text-amber-500 flex-shrink-0" data-id="element-239">
                        <Check size={12} className="md:w-3.5 md:h-3.5" data-id="element-240" />
                      </div>
                      <span className="text-base md:text-lg text-slate-200" data-id="element-241">
                        {item}
                      </span>
                    </motion.li>)}
                </ul>
                <p className="text-sm md:text-base text-slate-300 italic mb-6 md:mb-8" data-id="element-242">
                  Es el tipo de cosa que debería costar más… porque te evita la
                  parte más cara: improvisar agotada.
                </p>
                <Button className="w-full md:w-auto" onClick={scrollToPricing} data-id="element-243">
                  Quiero el mapa
                </Button>
                <p className="text-xs md:text-sm text-slate-400 mt-3 text-center md:text-left" data-id="element-244">
                  Lo abres a las 3 AM
                </p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* --- 8) WHO IT'S FOR / NOT FOR --- */}
      <Section className="!pt-2 md:!pt-20" data-id="element-245">
        <h2 className="text-2xl md:text-4xl font-bold text-slate-900 text-center mb-8 md:mb-12" data-id="element-246">
          ¿Es para ti?
        </h2>

        <div className="grid md:grid-cols-2 gap-6 md:gap-12" data-id="element-247">
          <div className="bg-teal-50 p-6 md:p-8 rounded-xl md:rounded-2xl border border-teal-100" data-id="element-248">
            <h3 className="text-xl md:text-2xl font-bold text-teal-900 mb-4 md:mb-6 flex items-center gap-2" data-id="element-249">
              <Check className="text-teal-600 w-5 h-5 md:w-6 md:h-6" data-id="element-250" /> Es para
              ti si…
            </h3>
            <ul className="space-y-3 md:space-y-4" data-id="element-251">
              <li className="flex gap-2 md:gap-3 text-sm md:text-base text-teal-800" data-id="element-252">
                <span className="font-bold" data-id="element-253">•</span> Te pesa la culpa por cómo
                reaccionas de noche
              </li>
              <li className="flex gap-2 md:gap-3 text-sm md:text-base text-teal-800" data-id="element-254">
                <span className="font-bold" data-id="element-255">•</span> Estás cansada de adivinar
                cada despertar
              </li>
              <li className="flex gap-2 md:gap-3 text-sm md:text-base text-teal-800" data-id="element-256">
                <span className="font-bold" data-id="element-257">•</span> Quieres estructura sin
                dureza ni gritos
              </li>
              <li className="flex gap-2 md:gap-3 text-sm md:text-base text-teal-800" data-id="element-258">
                <span className="font-bold" data-id="element-259">•</span> Quieres roles claros para
                no cargar sola
              </li>
            </ul>
          </div>

          <div className="bg-stone-50 p-6 md:p-8 rounded-xl md:rounded-2xl border border-stone-200" data-id="element-260">
            <h3 className="text-xl md:text-2xl font-bold text-slate-700 mb-4 md:mb-6 flex items-center gap-2" data-id="element-261">
              <X className="text-slate-400 w-5 h-5 md:w-6 md:h-6" data-id="element-262" /> No es para
              ti si…
            </h3>
            <ul className="space-y-3 md:space-y-4" data-id="element-263">
              <li className="flex gap-2 md:gap-3 text-sm md:text-base text-slate-600" data-id="element-264">
                <span className="font-bold" data-id="element-265">•</span> Buscas una promesa mágica
                tipo "12 horas garantizadas mañana"
              </li>
              <li className="flex gap-2 md:gap-3 text-sm md:text-base text-slate-600" data-id="element-266">
                <span className="font-bold" data-id="element-267">•</span> Quieres un método rígido
                que ignore a tu bebé y tu vida
              </li>
              <li className="flex gap-2 md:gap-3 text-sm md:text-base text-slate-600" data-id="element-268">
                <span className="font-bold" data-id="element-269">•</span> No estás dispuesta a
                sostener consistencia por al menos 7 noches
              </li>
            </ul>
          </div>
        </div>
      </Section>

      {/* --- 9) PRICING / OFFER --- */}
      <Section className="text-center !pt-2 pb-8 md:!pt-20 md:pb-20" id="pricing" data-id="element-270">
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="max-w-xl mx-auto" data-id="element-271">
          <p className="text-base md:text-lg text-slate-500 italic mb-4 md:mb-6" data-id="element-272">
            66 páginas diseñadas para usarse medio dormida.
          </p>

          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6 md:mb-8" data-id="element-273">
            Descárgalo hoy
          </h2>

          <div className="mb-6" data-id="element-274">
            <p className="text-slate-500 text-lg md:text-xl mb-2 flex items-center justify-center gap-3" data-id="element-275"></p>
            <h3 className="text-5xl md:text-7xl font-bold mb-3 md:mb-4" style={{
            color: COLORS.teal
          }} data-id="element-276">
              $6.39 USD
            </h3>
            <p className="text-teal-600 font-semibold text-sm md:text-base" data-id="element-277">
              Si te ahorra una sola discusión a las 3 AM, ya pagó
            </p>
          </div>

          <p className="text-lg md:text-xl text-slate-600 mb-6 md:mb-8" data-id="element-278">
            Pago único. Lo descargas y lo usas esta noche.
          </p>

          <Button fullWidth className="text-lg md:text-xl py-5 md:py-6 shadow-xl hover:shadow-2xl transform transition-all" onClick={handleCheckout} data-id="element-279">
            Quiero el mapa
          </Button>

          <p className="mt-4 md:mt-6 text-xs md:text-sm text-slate-400" data-id="element-280">
            Pago seguro • Entrega digital inmediata al correo electrónico
          </p>

          <p className="mt-3 md:mt-4 text-sm md:text-base text-amber-600 font-semibold" data-id="element-281">
            Este precio es de lanzamiento. Cuando cierre esta fase, vuelve a $27
            USD.
          </p>
        </motion.div>
      </Section>

      {/* --- SOCIAL PROOF BANNER --- */}
      <Section className="text-center !py-4 md:!py-12" data-id="element-282">
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-xl md:text-2xl font-bold text-slate-900" data-id="element-283">
          Cada semana más mamás lo están usando para dejar de improvisar.
        </motion.p>
      </Section>

      {/* --- 10) FAQ --- */}
      <Section className="bg-stone-50" data-id="element-284">
        <div className="max-w-2xl mx-auto" data-id="element-285">
          <h2 className="text-2xl md:text-4xl font-bold text-slate-900 text-center mb-8 md:mb-12" data-id="element-286">
            Preguntas Frecuentes
          </h2>

          <div className="space-y-3 md:space-y-4" data-id="element-287">
            <FaqItem question="¿Cómo recibo la guía?" answer="Después del pago en Shopify, te llega un email con el enlace de descarga y también aparece en la página de confirmación." data-id="element-288" />
            <FaqItem question="¿Dónde encuentro mi descarga si no veo el email?" answer="Revisa Spam/Promociones. También puedes descargarlo desde la pantalla de confirmación justo después de pagar." data-id="element-289" />
            <FaqItem question="¿Sirve si estoy lactando?" answer='Sí. El mapa separa "alimentación" vs "asociación" y te da guiones sin obligarte a cortar.' data-id="element-290" />
            <FaqItem question="¿Y si llora?" answer="Muchas veces es protesta. Aquí priorizamos calma y consistencia, no guerra." data-id="element-291" />
            <FaqItem question="¿Cuánto tarda?" answer="No hay noche mágica. En 7 noches deberías notar más claridad y menos caos." data-id="element-292" />
            <FaqItem question="¿Reemplaza al pediatra?" answer="No. Si hay fiebre alta o señales preocupantes, consulta." data-id="element-293" />
          </div>
        </div>
      </Section>

      {/* --- ACLARACIONES --- */}
      <div className="py-6 md:py-14 px-4 md:px-6" style={{
      backgroundColor: '#f0ede8'
    }} data-id="element-294">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} className="max-w-2xl mx-auto text-center" data-id="element-295">
          <motion.h3 variants={itemVariant} className="text-lg md:text-xl font-bold text-slate-800 mb-6 md:mb-8" data-id="element-296">
            Este producto:
          </motion.h3>
          <div className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-4" data-id="element-297">
            {['No reemplaza pediatra', 'No es método rígido', 'No es cry-it-out obligatorio'].map((item, i) => <motion.div key={i} variants={itemVariant} className="flex items-center gap-2.5 px-5 py-3 rounded-full bg-white border border-stone-200 shadow-sm" data-id="element-298">
                <div className="w-5 h-5 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0" data-id="element-299">
                  <X size={11} strokeWidth={2.5} className="text-stone-400" data-id="element-300" />
                </div>
                <span className="text-sm md:text-base text-slate-700 font-medium whitespace-nowrap" data-id="element-301">
                  {item}
                </span>
              </motion.div>)}
          </div>
        </motion.div>
      </div>

      {/* --- 11) FINAL CTA --- */}
      <Section dark className="text-center" data-id="element-302">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="max-w-2xl mx-auto" data-id="element-303">
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4 md:mb-6" data-id="element-304">
            Tu yo de las 3 AM no necesita otro consejo. Necesita{' '}
            <span style={{
            color: COLORS.amber
          }} data-id="element-305">
              un guión
            </span>
            .
          </h2>
          <p className="text-lg md:text-xl text-slate-300 mb-4 md:mb-6" data-id="element-306">
            Esta noche se va a despertar. Eso no lo puedes controlar.
          </p>
          <p className="text-lg md:text-xl text-slate-300 mb-8 md:mb-10" data-id="element-307">
            Lo que sí puedes controlar es si vas a improvisar otra vez o si vas
            a tener <strong className="text-white" data-id="element-308">un plan</strong>.
          </p>

          <Button className="w-full md:w-auto md:min-w-[280px]" onClick={handleCheckout} data-id="element-309">
            Quiero el mapa <ArrowRight className="w-4 h-4 md:w-5 md:h-5" data-id="element-310" />
          </Button>

          <p className="mt-4 md:mt-6 text-xs md:text-sm text-slate-500" data-id="element-311">
            PDF en español • Descarga inmediata • Acceso de por vida
          </p>
        </motion.div>
      </Section>

      {/* --- 12) FOOTER --- */}
      <footer className="bg-slate-900 text-slate-500 py-8 md:py-10 text-center text-xs md:text-sm border-t border-slate-800" data-id="element-312">
        <div className="max-w-4xl mx-auto px-4 md:px-6" data-id="element-313">
          <p className="mb-4" data-id="element-314">
            Este producto no sustituye el consejo médico profesional. Siempre
            consulta con tu pediatra.
          </p>
          <div className="flex justify-center gap-6 text-slate-400 mb-4" data-id="element-315">
            <button onClick={() => setModalContent('privacy')} className="hover:text-slate-300 transition-colors underline" data-id="element-316">
              Política de Privacidad
            </button>
            <button onClick={() => setModalContent('terms')} className="hover:text-slate-300 transition-colors underline" data-id="element-317">
              Términos y Condiciones
            </button>
          </div>
          <p data-id="element-318">
            &copy; {new Date().getFullYear()} Bebé Duerme, Mamá Vive. Todos los
            derechos reservados.
          </p>
        </div>
      </footer>

      {/* --- MODALS --- */}
      <Modal isOpen={modalContent === 'privacy'} onClose={() => setModalContent(null)} title="Política de Privacidad" data-id="element-319">
        <div className="prose prose-slate max-w-none text-sm md:text-base" data-id="element-320">
          <p className="text-slate-600 mb-4" data-id="element-321">
            <strong data-id="element-322">Última actualización:</strong>{' '}
            {new Date().toLocaleDateString('es-ES')}
          </p>
          <h3 className="text-lg font-bold text-slate-900 mt-6 mb-3" data-id="element-323">
            1. Información que Recopilamos
          </h3>
          <p className="text-slate-700 mb-4" data-id="element-324">
            Al adquirir "Bebé Duerme, Mamá Vive", recopilamos únicamente la
            información necesaria para procesar tu compra y entregarte el
            producto digital:
          </p>
          <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4" data-id="element-325">
            <li data-id="element-326">Nombre y dirección de correo electrónico</li>
            <li data-id="element-327">
              Información de pago (procesada de forma segura por nuestro
              procesador de pagos)
            </li>
            <li data-id="element-328">
              Dirección IP y datos de navegación para prevención de fraude
            </li>
          </ul>
          <h3 className="text-lg font-bold text-slate-900 mt-6 mb-3" data-id="element-329">
            2. Uso de la Información
          </h3>
          <p className="text-slate-700 mb-4" data-id="element-330">
            Utilizamos tu información exclusivamente para:
          </p>
          <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4" data-id="element-331">
            <li data-id="element-332">Procesar tu compra y enviarte el PDF descargable</li>
            <li data-id="element-333">Enviarte el recibo de compra y confirmación de pedido</li>
            <li data-id="element-334">Proporcionar soporte técnico si lo solicitas</li>
            <li data-id="element-335">Cumplir con obligaciones legales y fiscales</li>
          </ul>
          <h3 className="text-lg font-bold text-slate-900 mt-6 mb-3" data-id="element-336">
            3. Protección de Datos
          </h3>
          <p className="text-slate-700 mb-4" data-id="element-337">
            Implementamos medidas de seguridad técnicas y organizativas para
            proteger tu información personal contra acceso no autorizado,
            pérdida o alteración.
          </p>
          <h3 className="text-lg font-bold text-slate-900 mt-6 mb-3" data-id="element-338">
            4. Contacto
          </h3>
          <p className="text-slate-700" data-id="element-339">
            Para ejercer tus derechos o realizar consultas sobre privacidad,
            contáctanos a través del correo electrónico proporcionado en tu
            recibo de compra.
          </p>
        </div>
      </Modal>

      <Modal isOpen={modalContent === 'terms'} onClose={() => setModalContent(null)} title="Términos y Condiciones" data-id="element-340">
        <div className="prose prose-slate max-w-none text-sm md:text-base" data-id="element-341">
          <p className="text-slate-600 mb-4" data-id="element-342">
            <strong data-id="element-343">Última actualización:</strong>{' '}
            {new Date().toLocaleDateString('es-ES')}
          </p>
          <h3 className="text-lg font-bold text-slate-900 mt-6 mb-3" data-id="element-344">
            1. Aceptación de Términos
          </h3>
          <p className="text-slate-700 mb-4" data-id="element-345">
            Al adquirir "Bebé Duerme, Mamá Vive", aceptas estos términos y
            condiciones en su totalidad.
          </p>
          <h3 className="text-lg font-bold text-slate-900 mt-6 mb-3" data-id="element-346">
            2. Descripción del Producto
          </h3>
          <p className="text-slate-700 mb-4" data-id="element-347">
            "Bebé Duerme, Mamá Vive" es una guía digital en formato PDF que
            proporciona herramientas para manejar los despertares nocturnos de
            tu bebé.
          </p>
          <h3 className="text-lg font-bold text-slate-900 mt-6 mb-3" data-id="element-348">
            3. Precio y Pago
          </h3>
          <p className="text-slate-700 mb-4" data-id="element-349">
            El precio del producto es de $6.39 USD. Este es un pago único que te
            otorga acceso permanente al PDF.
          </p>
          <h3 className="text-lg font-bold text-slate-900 mt-6 mb-3" data-id="element-350">
            4. Entrega del Producto
          </h3>
          <p className="text-slate-700 mb-4" data-id="element-351">
            El PDF será enviado inmediatamente a tu correo electrónico tras
            confirmar el pago.
          </p>
          <h3 className="text-lg font-bold text-slate-900 mt-6 mb-3" data-id="element-352">
            5. Descargo de Responsabilidad Médica
          </h3>
          <p className="text-slate-700 mb-4 font-semibold" data-id="element-353">
            IMPORTANTE: Este producto NO sustituye el consejo médico
            profesional. Siempre consulta con tu pediatra.
          </p>
          <h3 className="text-lg font-bold text-slate-900 mt-6 mb-3" data-id="element-354">
            6. Contacto
          </h3>
          <p className="text-slate-700" data-id="element-355">
            Para preguntas sobre estos términos, contáctanos a través del correo
            electrónico proporcionado en tu recibo de compra.
          </p>
        </div>
      </Modal>
    </div>;
}